import React from "react";
import { Container, Row, Col, Image, Card, Button } from "react-bootstrap";
import {
  FaUserPlus,
  FaQrcode,
  FaWhatsapp,
  FaChartLine,
  FaMoneyBillWave,
  FaMobileAlt,
  FaClipboardList,
  FaUtensils,
  FaWallet,
  FaBoxOpen,
  FaHeadset,
  FaRunning,
  FaHeartbeat,
  FaBiking,
  FaUsers,
  FaUserShield,
  FaLaptop,
  FaApple,
} from "react-icons/fa";

import { LiaDumbbellSolid } from "react-icons/lia";

import dashboardPic from "../assets/desktop_image.png";
import Seo from "../components/Seo";

const themeColor = "#0a5a6a";
const accentColor = "#1091ab";

const features = [
  {
    icon: <FaUserPlus />,
    title: "Member Management",
    text: "Add members in seconds, track active and expired memberships, and keep every member profile — photos, plans and payments — in one place.",
  },
  {
    icon: <FaQrcode />,
    title: "QR Code Attendance & Check-In",
    text: "Touch-free check-in and check-out with attendance QR codes. Know exactly who is in your gym, every hour of the day.",
  },
  {
    icon: <FaWhatsapp />,
    title: "WhatsApp Reminders & Alerts",
    text: "Automatic WhatsApp messages for fee reminders, renewals and birthday wishes. Recover pending payments without awkward phone calls.",
  },
  {
    icon: <FaMoneyBillWave />,
    title: "Payments & Pending Fee Tracking",
    text: "Complete payment history, pending payment alerts and instant receipts. Never lose track of a single rupee again.",
  },
  {
    icon: <FaChartLine />,
    title: "Smart Analytics Dashboard",
    text: "Live counts of total, male, female, senior and PT members, package-wise breakups and revenue trends — your gym's health at a glance.",
  },
  {
    icon: <FaBoxOpen />,
    title: "Flexible Packages & Subscriptions",
    text: "Create monthly, quarterly, half-yearly, yearly or custom challenge packages. Renew subscriptions with a single click.",
  },
  {
    icon: <FaWallet />,
    title: "Accounts & Expense Management",
    text: "Track income and expenses side by side and know your real profit — not just your collections.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile App for Members",
    text: "Give members their own app to view plans, attendance and payment due dates — a professional touch that keeps them engaged.",
  },
  {
    icon: <LiaDumbbellSolid />,
    title: "Personal Training Management",
    text: "Manage PT members and their trainers separately, with dedicated packages and schedules for personal training revenue.",
  },
  {
    icon: <FaClipboardList />,
    title: "Enquiry Tracker",
    text: "Capture every walk-in and phone enquiry, follow up on time and convert more leads into paying members.",
  },
  {
    icon: <FaUtensils />,
    title: "Workout & Diet Plans",
    text: "Assign workout routines and diet plans to members digitally — help them see results and stay subscribed longer.",
  },
  {
    icon: <FaHeadset />,
    title: "Dedicated Support",
    text: "Friendly onboarding and support on WhatsApp and phone. We help you migrate your existing member data for free.",
  },
];

const pricingPlans = [
  {
    name: "Premium Package",
    originalprice: "₹1250",
    price: "₹499",
    period: "/month",
    tagline: "just",
    popular: false,
    points: [
      "Member Management",
      "Payment History",
      "Pending Payments Tracker",
      "Automatic WhatsApp Reminders",
      "Mobile App For Gym Members",
      "Mobile App For Gym Admins",
      "Biometric integration ",
      "Workout Plans",
      "Diet Plans",
      "Attendance Management",
      "Custom Packages",
      "Smart Analytics",
      "Attendance Using QR code",
      "Member Self Subscription using QR",
      "Accounts & Expense",
      "Enquiry Tracker",
      "Equipments Management",
      "Staff Attendance",
      "Admin Management",
      "Gym Events Managements",
    ],
  },
];

// Multicolor Google Play triangle matching the official store logo colors.
const GooglePlayLogo = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <polygon fill="#00D7FE" points="3,1.5 13.2,12 3,22.5" />
    <polygon fill="#00F076" points="3,1.5 17.3,8.4 13.2,12" />
    <polygon fill="#FF3A44" points="3,22.5 13.2,12 17.3,15.6" />
    <polygon fill="#FFD500" points="13.2,12 17.3,8.4 22,12 17.3,15.6" />
  </svg>
);

// Faint workout icons scattered behind a section's content, watermark style.
const Watermark = ({ items, color = accentColor, opacity = 0.14 }) => (
  <div className="watermark-layer" aria-hidden="true">
    {items.map(({ Icon, pos, size, rotate = 0 }, i) => (
      <Icon
        key={i}
        className="watermark-icon"
        style={{
          ...pos,
          fontSize: size,
          color,
          opacity,
          transform: `rotate(${rotate}deg)`,
        }}
      />
    ))}
  </div>
);

const sectionHeading = {
  color: themeColor,
  fontWeight: 700,
  marginBottom: "0.5rem",
};

const sectionSub = {
  color: "#555",
  maxWidth: "720px",
  margin: "0 auto 1.25rem",
};

const HomeScreen = () => {
  return (
    <>
      <Seo
        title="Best Gym Management Software in India | Gym, Fitness Centre & Fitness Studio Software | zpotter"
        description="zpotter is the best gym management software and fitness centre management system — gym billing, membership, attendance and ERP software in one. For Gyms, Fitness Centres, Fitness Studios, Fitness Clubs, CrossFit Boxes, Yoga and Dance Studios. 30 days free trial."
        path="/"
      />
      {/* ---------- Hero ---------- */}
      <section
        className="hero-section section-relative"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${accentColor} 100%)`,
          color: "#fff",
        }}
      >
        <Watermark
          color="#ffffff"
          opacity={0.16}
          items={[
            {
              Icon: LiaDumbbellSolid,
              pos: { bottom: "-40px", left: "-30px" },
              size: "14rem",
              rotate: -20,
            },
            {
              Icon: FaRunning,
              pos: { top: "20px", right: "8%" },
              size: "7rem",
              rotate: 10,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "45%", right: "-45px" },
              size: "10rem",
              rotate: 30,
            },
          ]}
        />
        <Container className="content-above">
          <Row className="align-items-center">
            <Col lg={6} className="mb-1 mb-lg-0">
              <h1 className="hero-title">
                Gym Management Software That Runs Your Gym While You Train Your
                Members
              </h1>
              <p
                className="hero-sub"
                style={{
                  marginTop: "1rem",
                  opacity: 0.95,
                }}
              >
                zpotter is an all-in-one gym management system for Gyms, Fitness
                studios, Health clubs, CrossFit Boxes, Yoga and Dance Studios in
                India. Manage memberships, fees, attendance, WhatsApp reminders
                and analytics — all from one simple dashboard.
              </p>
              <div
                className="hero-cta"
                style={{
                  marginTop: "0.5rem",
                }}
              >
                <Button
                  href="#pricing"
                  size="lg"
                  style={{
                    backgroundColor: "#fff",
                    color: themeColor,
                    border: "none",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  View Pricing
                </Button>
                <Button
                  href="https://wa.me/917806873973?text=Hi%2C%20I%20want%20a%20free%20demo%20of%20zpotter%20gym%20management%20software"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="lg"
                  style={{
                    backgroundColor: "#25D366",
                    border: "none",
                    color: "#fff",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  <FaWhatsapp style={{ marginRight: "0.5rem" }} />
                  Get Free Demo
                </Button>
              </div>
            </Col>
            <Col lg={6}>
              <Image
                src={dashboardPic}
                alt="zpotter gym management software dashboard showing member management, attendance and payment tracking"
                fluid
                rounded
                style={{ boxShadow: "0 15px 40px rgba(0,0,0,0.35)" }}
              />
            </Col>
            <Row>
              <div
                className="hero-badges"
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {[
                  "30 days Free trial",
                  "No hidden charges",
                  "Members Transfer Available",
                  "24/7 Support",
                ].map((point) => (
                  <span key={point} className="hero-badge">
                    ✔ {point}
                  </span>
                ))}
              </div>
            </Row>
          </Row>
        </Container>
      </section>
      {/* ---------- Pricing ---------- */}
      <section
        id="pricing"
        className="section-pad section-relative"
        style={{ backgroundColor: "#fff" }}
      >
        <Watermark
          items={[
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "30px", right: "-50px" },
              size: "12rem",
              rotate: 25,
            },
            {
              Icon: FaHeartbeat,
              pos: { bottom: "40px", left: "-40px" },
              size: "10rem",
              rotate: -10,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "8%", left: "6%" },
              size: "8rem",
              rotate: -15,
            },
          ]}
        />
        <Container className="content-above">
          <div className="text-center">
            <h2 className="section-heading" style={sectionHeading}>
              Simple, Honest Pricing
            </h2>
            <h6 style={sectionSub}>
              One plan, All features included — less than the cost of a single
              membership per month.
            </h6>
          </div>
          <Row className="justify-content-center">
            <Col xs={12} md={11} lg={10} xl={9} className="mb-4">
              <Card
                style={{
                  borderRadius: "16px",
                  border: `2px solid ${accentColor}`,
                  boxShadow: "0 10px 30px rgba(16,145,171,0.25)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    backgroundColor: accentColor,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "0.4rem",
                    textAlign: "center",
                    letterSpacing: "1px",
                  }}
                >
                  ALL PREMIUM FEATURES INCLUDED
                </div>
                <Card.Body style={{ padding: "0.5rem" }}>
                  <Row>
                    {/* Price panel */}
                    <Col
                      md={4}
                      className="text-center mb-1 mb-md-0 d-flex flex-column justify-content-center"
                    >
                      <h3 style={{ color: themeColor, fontWeight: 800 }}>
                        {pricingPlans[0].name}
                      </h3>
                      <div style={{ margin: "0.75rem 0" }}>
                        <div>
                          <span
                            className="price-old"
                            style={{ fontSize: "2rem" }}
                          >
                            {pricingPlans[0].originalprice}
                          </span>
                        </div>
                        <span
                          style={{
                            color: "#777",
                            fontSize: "1rem",
                            marginRight: "0.4rem",
                          }}
                        >
                          {pricingPlans[0].tagline}
                        </span>
                        <span className="price-now">
                          {pricingPlans[0].price}
                        </span>
                        <span style={{ color: "#777" }}>
                          {pricingPlans[0].period}
                        </span>
                        <span style={{ color: "#777" }}>+GST</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          alignItems: "center",
                          marginBottom: "1rem",
                        }}
                      >
                        {[
                          "No Initial Setup Fee",
                          "No Hidden Charges",
                          // "Free Member Transfer",
                          "Cancel Anytime",
                        ].map((badge) => (
                          <span key={badge} className="price-badge">
                            ✔ {badge}
                          </span>
                        ))}
                      </div>
                      <Button
                        href="https://wa.me/917806873973?text=Hi%2C%20I%20want%20a%20free%20demo%20of%20zpotter%20gym%20management%20software"
                        target="_blank"
                        rel="noopener noreferrer"
                        size="lg"
                        style={{
                          backgroundColor: "#25D366",
                          border: "none",
                          color: "#fff",
                          fontWeight: 700,
                          textDecoration: "underline",
                        }}
                      >
                        {" "}
                        <FaWhatsapp style={{ marginRight: "0.5rem" }} />
                        Start Free Trial
                      </Button>
                    </Col>
                    {/* Feature list */}
                    <Col md={8}>
                      <Row
                        as="ul"
                        style={{ listStyle: "none", padding: 0, margin: 0 }}
                      >
                        {pricingPlans[0].points.map((p) => (
                          <Col
                            as="li"
                            xs={12}
                            sm={6}
                            key={p}
                            style={{
                              padding: "0.4rem 0.75rem",
                              fontSize: "0.95rem",
                              color: "#0a0a0a",
                            }}
                          >
                            <span
                              style={{
                                color: accentColor,
                                marginRight: "0.5rem",
                              }}
                            >
                              ✔
                            </span>
                            {p}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                  <hr
                    style={{
                      borderColor: "#cfe7ec",
                      opacity: 1,
                      margin: "1rem 0",
                    }}
                  />
                  <div className="plan-highlights">
                    {[
                      { Icon: FaUsers, label: "Unlimited Members" },
                      { Icon: FaUserShield, label: "Multiple Admins" },
                      { Icon: FaLaptop, label: "Unlimited Device Logins" },
                    ].map(({ Icon, label }) => (
                      <div key={label} className="plan-highlight-item">
                        <Icon
                          style={{ color: accentColor, fontSize: "1.5rem" }}
                        />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <p
            className="text-center"
            style={{ color: "#777", fontSize: "0.9rem" }}
          >
            Prices exclude GST. for more details, Please reach us on WhatsApp.
          </p>
        </Container>
      </section>

      {/* ---------- App Download ---------- */}
      <section
        id="download"
        className="section-pad section-relative"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${accentColor} 100%)`,
          color: "#fff",
        }}
      >
        <Container className="content-above text-center">
          <h2
            className="section-heading"
            style={{ color: "#fff", fontWeight: 700 }}
          >
            Take Your Gym With You — Download the App
          </h2>
          <p
            style={{
              maxWidth: "720px",
              margin: "0.5rem auto 1.25rem",
              opacity: 0.95,
            }}
          >
            Manage your gym on the go, Everything on the mobile, available on
            Android and iPhone.
          </p>
          <div className="store-badges">
            <a
              href="https://play.google.com/store/apps/details?id=in.zpotter.www&hl=en_IN"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
              aria-label="Get the zpotter app on Google Play"
            >
              <GooglePlayLogo size={32} />
              <span className="store-badge-text">
                <small>GET IT ON</small>
                <strong>Google Play</strong>
              </span>
            </a>
            <a
              href="https://apps.apple.com/in/app/zpotter/id6740035107"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
              aria-label="Download the zpotter app on the App Store"
            >
              <FaApple style={{ fontSize: "2.3rem", color: "#0D96F6" }} />
              <span className="store-badge-text">
                <small>Download on the</small>
                <strong>App Store</strong>
              </span>
            </a>
          </div>
        </Container>
      </section>

      {/* ---------- About ---------- */}
      <section
        id="about"
        className="section-pad section-relative"
        style={{ backgroundColor: "#fff" }}
      >
        <Watermark
          items={[
            {
              Icon: FaBiking,
              pos: { top: "15%", right: "-45px" },
              size: "11rem",
              rotate: -8,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { bottom: "-30px", left: "10%" },
              size: "9rem",
              rotate: 15,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "5%", left: "40%" },
              size: "7rem",
              rotate: 40,
            },
          ]}
        />
        <Container className="content-above">
          {/* <div className="text-center">
            <h2 className="section-heading" style={sectionHeading}>
              About zpotter
            </h2>
            <p style={sectionSub}>
              Built by zpotter technologies, trusted by gym owners who want to
              spend less time on paperwork and more time on the gym floor.
            </p>
          </div> */}
          <Row className="align-items-center">
            <Col md={6} className="mb-4 mb-md-0">
              <h3 style={{ color: themeColor, fontWeight: 700 }}>
                Why gym owners switch to zpotter
              </h3>
              <p>
                Spend less time managing your gym and more time growing it.
                zpotter brings members, attendance, subscriptions, payments, and
                reports together in one powerful, easy-to-use platform.
              </p>

              <p>
                We are a proudly Indian software company based in Chennai, and
                we build for Indian gyms: WhatsApp-first communication, INR
                pricing, flexible desi packages like 30-day challenges, and
                support in your language.
              </p>
            </Col>
            <Col md={6}>
              <Row>
                {[
                  ["2000+", "Members managed per gym"],
                  ["24×7", "Cloud access on any device"],
                  ["1-click", "Fee reminders on WhatsApp"],
                  ["100%", "Free onboarding & migration"],
                ].map(([stat, label]) => (
                  <Col xs={6} key={label} className="mb-3">
                    <Card
                      className="text-center h-100 lift-card"
                      style={{
                        border: `1px solid #e0eef1`,
                        borderRadius: "12px",
                      }}
                    >
                      <Card.Body>
                        <div
                          style={{
                            fontSize: "1.8rem",
                            fontWeight: 800,
                            color: accentColor,
                          }}
                        >
                          {stat}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: "#555" }}>
                          {label}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>
      <hr style={{ borderColor: "#1091ab" }} />
      {/* ---------- Features ---------- */}
      <section
        id="features"
        className="section-pad section-relative"
        style={{ backgroundColor: "#f2f8f9" }}
      >
        <Watermark
          items={[
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "-30px", left: "-40px" },
              size: "13rem",
              rotate: -25,
            },
            {
              Icon: FaRunning,
              pos: { bottom: "10%", right: "-30px" },
              size: "10rem",
              rotate: 8,
            },
            {
              Icon: FaHeartbeat,
              pos: { top: "40%", left: "45%" },
              size: "8rem",
              rotate: 12,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { bottom: "-35px", left: "30%" },
              size: "10rem",
              rotate: -18,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "25%", right: "20%" },
              size: "7rem",
              rotate: 22,
            },
          ]}
        />
        <Container className="content-above">
          <div className="text-center">
            <h2 className="section-heading" style={sectionHeading}>
              Everything Your Gym Needs, In One Software
            </h2>
            <p style={sectionSub}>
              From the front desk to the trainer floor to your accounts book —
              zpotter covers the complete daily life of a fitness business.
            </p>
          </div>
          <Row>
            {features.map((f) => (
              <Col sm={6} lg={4} key={f.title} className="mb-4">
                <Card
                  className="h-100 lift-card"
                  style={{
                    border: "none",
                    borderRadius: "14px",
                    boxShadow: "0 4px 14px rgba(10,90,106,0.08)",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        color: accentColor,
                        fontSize: "1.8rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {f.icon}{" "}
                      <span
                        style={{
                          color: themeColor,
                          fontSize: "1.2rem",
                          marginBottom: "0.75rem",
                          fontWeight: 700,
                        }}
                      >
                        {f.title}
                      </span>
                    </div>

                    <Card.Text style={{ fontSize: "0.95rem", color: "#555" }}>
                      {f.text}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
      <hr style={{ borderColor: "#1091ab" }} />
      {/* ---------- SEO content: one software, every fitness business ---------- */}
      <section className="section-pad" style={{ backgroundColor: "#fff" }}>
        <Container>
          <div className="text-center">
            <h2 className="section-heading" style={sectionHeading}>
              One Software For Every Fitness Business
            </h2>
          </div>
          <Row className="justify-content-center">
            <Col lg={10}>
              <p style={{ color: "#555" }}>
                Whether you call it gym software, a gym management system or a
                complete gym ERP, zpotter brings everything under one roof. It
                works as your gym billing software for fee collection and
                receipts, your gym membership software for plans and renewals,
                and your gym attendance software with QR code check-ins — no
                separate tools, no juggling spreadsheets.
              </p>
              <p style={{ color: "#555" }}>
                Run a fitness centre instead of a gym? zpotter is equally at
                home as fitness centre management software — handling fitness
                centre billing, membership and attendance for single locations
                and multi-branch fitness clubs alike. Health clubs and fitness
                club owners use it as their day-to-day fitness club software to
                track members, payments and staff from one dashboard.
              </p>
              <p style={{ color: "#555" }}>
                Fitness studios love it too. As fitness studio management
                software, zpotter manages class packages, personal training,
                workout and diet plans for yoga studios, dance studios, CrossFit
                boxes and boutique fitness studios. If you are searching for the
                best gym software, best fitness centre software or best fitness
                studio software in India at an honest price — start your 30-day
                free trial and see why gym owners switch to zpotter.
              </p>
            </Col>
          </Row>
        </Container>
      </section>
      <hr style={{ borderColor: "#1091ab" }} />
      {/* ---------- Contact ---------- */}
      <section
        id="contact"
        className="section-pad section-relative"
        style={{ backgroundColor: "#f2f8f9" }}
      >
        <Watermark
          items={[
            {
              Icon: LiaDumbbellSolid,
              pos: { bottom: "-35px", right: "5%" },
              size: "11rem",
              rotate: 20,
            },
            {
              Icon: FaBiking,
              pos: { top: "20px", left: "-40px" },
              size: "9rem",
              rotate: -12,
            },
            {
              Icon: LiaDumbbellSolid,
              pos: { top: "10%", right: "40%" },
              size: "7rem",
              rotate: -25,
            },
          ]}
        />
        <Container className="content-above">
          <div className="text-center">
            <h2 className="section-heading" style={sectionHeading}>
              Talk to Us — Get a Free Demo
            </h2>
            <p style={sectionSub}>
              See zpotter running with your own gym's data in a 15-minute demo.
              Call, WhatsApp or email us — we reply fast.
            </p>
          </div>
          <Row className="justify-content-center">
            <Col sm={10} md={6} lg={5} className="mb-4">
              <Card
                className="h-100"
                style={{
                  border: "none",
                  borderRadius: "14px",
                  boxShadow: "0 4px 14px rgba(10,90,106,0.08)",
                }}
              >
                <Card.Body>
                  <h3
                    style={{
                      fontSize: "1.15rem",
                      color: themeColor,
                      fontWeight: 700,
                    }}
                  >
                    Reach Us
                  </h3>
                  <p style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: themeColor }}>
                      WhatsApp/Call:
                    </strong>{" "}
                    <br />
                    <a href="tel:+917806873973" style={{ color: accentColor }}>
                      +91 78068 73973
                    </a>
                    {", "}
                    <a href="tel:+919092913756" style={{ color: accentColor }}>
                      +91 6382131216
                    </a>
                  </p>
                  <p style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: themeColor }}>Email:</strong>{" "}
                    <a
                      href="mailto:Contactus@zpotter.in"
                      style={{ color: accentColor }}
                    >
                      contactus@zpotter.in
                    </a>
                  </p>
                  <p style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: themeColor }}>
                      Working Hours:
                    </strong>
                    <br />
                    Monday to Saturday, 9 AM – 8 PM
                  </p>
                  <Button
                    href="https://wa.me/917806873973?text=Hi%2C%20I%20want%20a%20free%20demo%20of%20zpotter%20gym%20management%20software"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      backgroundColor: "#25D366",
                      border: "none",
                      fontWeight: 700,
                      marginTop: "0.5rem",
                    }}
                  >
                    <FaWhatsapp style={{ marginRight: "0.5rem" }} />
                    Chat on WhatsApp
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={10} md={6} lg={5} className="mb-4">
              <Card
                className="h-100"
                style={{
                  border: "none",
                  borderRadius: "14px",
                  boxShadow: "0 4px 14px rgba(10,90,106,0.08)",
                }}
              >
                <Card.Body>
                  <h3
                    style={{
                      fontSize: "1.15rem",
                      color: themeColor,
                      fontWeight: 700,
                    }}
                  >
                    Contact Address
                  </h3>
                  <p style={{ marginBottom: "1rem" }}>
                    No 7, Vivekanandar Street,
                    <br />
                    Rajakilpakam, Chennai – 600073.
                  </p>
                  <p style={{ color: "#777", fontSize: "0.9rem" }}>
                    GSTIN: 33DFNPJ2709J1ZC
                  </p>
                  {/* <h3
                    style={{
                      fontSize: "1.15rem",
                      color: themeColor,
                      fontWeight: 700,
                    }}
                  >
                    Registered Office
                  </h3>
                  <p style={{ marginBottom: "0.5rem" }}>
                    No 301/5, New Colony, Keelairal, Ettayapuram,
                    <br />
                    Tuticorin, Tamil Nadu – 628908.
                  </p>
                  <p style={{ color: "#777", fontSize: "0.9rem" }}>
                    GSTIN: 33DFNPJ2709J1ZC
                  </p> */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default HomeScreen;
